/**
 * Data hook for fetching chat details
 * Separates GraphQL operations from UI components
 */

import type { ChatDetailsResponse, EditChatConfig } from '@choco-admin/types';
import { useCallback, useEffect, useState, useRef } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useGetChatDataQuery } from '../../../generated';
import { mapToChatDetailsResponse } from '../utils';

export interface UseGetChatDataResponse {
  chatData: ChatDetailsResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useGetChatData = (
  chatId: string,
  config: EditChatConfig = {}
): UseGetChatDataResponse => {
  const { onError, captureException } = config;
  const [chatData, setChatData] = useState<ChatDetailsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { addNotification } = useNotification();

  // Use refs to store callback functions to avoid dependency issues
  const onErrorRef = useRef(onError);
  const captureExceptionRef = useRef(captureException);

  // Update refs when callbacks change
  useEffect(() => {
    onErrorRef.current = onError;
    captureExceptionRef.current = captureException;
  }, [onError, captureException]);

  const {
    data,
    loading,
    error: gqlError,
    refetch: gqlRefetch,
  } = useGetChatDataQuery({
    variables: {
      chatId,
      first: 30,
      withAllUserFields: true,
      withProducts: true,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Map GraphQL data to domain types
  useEffect(() => {
    if (data?.chat) {
      try {
        const mappedData = mapToChatDetailsResponse(
          data.chat,
          data.getSubCatalogsForChat
        );
        setChatData(mappedData);
        setError(null);
      } catch (mappingError) {
        const err = new Error(`Failed to map chat data: ${mappingError}`);
        setError(err);
        onErrorRef.current?.(err);
        captureExceptionRef.current?.(err);
      }
    }
  }, [data]);

  // Handle GraphQL errors
  useEffect(() => {
    if (gqlError) {
      const err = new Error(`Failed to fetch chat data: ${gqlError.message}`);
      setError(err);
      onErrorRef.current?.(err);
      captureExceptionRef.current?.(err);

      addNotification({
        title: 'Error Loading Chat',
        message: 'Failed to load chat details. Please try again.',
        type: 'error',
      });
    }
  }, [gqlError, addNotification]);

  const refetch = useCallback(async () => {
    try {
      setError(null);
      await gqlRefetch();
    } catch (refetchError) {
      const err = new Error(`Failed to refetch chat data: ${refetchError}`);
      setError(err);
      onErrorRef.current?.(err);
      captureExceptionRef.current?.(err);
    }
  }, [gqlRefetch]);

  return {
    chatData,
    isLoading: loading,
    error,
    refetch,
  };
};
